import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Megaphone, Sparkles, QrCode, Copy, Phone, RotateCcw, Trash2, Archive, ListChecks,
} from "lucide-react";
import { sr } from "../../../lib/sr";
import { auth } from "../../../lib/api";
import { Head, Honest } from "./shared";

// Honest status tone -> badge class (Pending Review / Scheduled / Live / Needs Attention).
const TONE = { amber: "sr-badge-amber", blue: "sr-badge-blue", green: "sr-badge-green", red: "sr-badge-red", gray: "sr-badge-gray" };
export function Campaigns() {
  const [camps, setCamps] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const user = auth.getUser();
  const isStaff = user && ["admin", "super_admin", "campaign_manager", "compliance_reviewer"].includes(user.role);
  const load = (incArchived) => sr.campaigns(incArchived).then((r) => setCamps(r.campaigns)).catch(() => setCamps([]));
  useEffect(() => { load(showArchived); }, [showArchived]);
  const reload = () => { setSelected(new Set()); load(showArchived); };
  const toggleSelect = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const bulk = async (permanent) => {
    const ids = [...selected];
    if (!ids.length) return;
    const verb = permanent ? "permanently delete" : "archive";
    if (!window.confirm(`${permanent ? "Permanently delete" : "Archive"} ${ids.length} campaign(s)? ${permanent ? "This cannot be undone." : "You can restore them later."}`)) return;
    setBulkBusy(true);
    try {
      await sr.bulkDeleteCampaigns(ids, permanent);
      toast.success(`${ids.length} campaign(s) ${permanent ? "deleted" : "archived"}`);
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || `Could not ${verb}`); }
    finally { setBulkBusy(false); }
  };
  if (!camps) return <div className="sr-empty"><Loader2 className="sr-spin" /></div>;
  const selectable = camps.filter((c) => c.status !== "ARCHIVED");
  const allSelected = selectable.length > 0 && selectable.every((c) => selected.has(c.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectable.map((c) => c.id)));
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        <Head title="Campaigns" sub="Your live and submitted campaigns, budgets, status and response tracking." />
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setShowArchived((v) => !v)} data-testid="toggle-archived-btn">
            <Archive size={14} /> {showArchived ? "Hide archived" : "Show archived"}
          </button>
          <Link to="/app/campaigns/new" className="sr-btn sr-btn-primary sr-btn-sm" data-testid="create-with-ai-btn">
            <Sparkles size={15} /> Create with AI
          </Link>
        </div>
      </div>

      {selectable.length > 0 && (
        <div className="sr-card" style={{ marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }} data-testid="bulk-action-bar">
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "var(--navy)", fontWeight: 600 }}>
            <input type="checkbox" checked={allSelected} onChange={toggleAll} data-testid="bulk-select-all" />
            {selected.size > 0 ? `${selected.size} selected` : "Select all"}
          </label>
          {selected.size > 0 && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => bulk(false)} disabled={bulkBusy} data-testid="bulk-archive-btn">
                {bulkBusy ? <Loader2 size={14} className="sr-spin" /> : <Archive size={14} />} Archive selected
              </button>
              {isStaff && (
                <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => bulk(true)} disabled={bulkBusy} data-testid="bulk-delete-btn" style={{ color: "#C0362C" }}>
                  <Trash2 size={14} /> Delete selected
                </button>
              )}
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setSelected(new Set())} data-testid="bulk-clear-btn">Clear</button>
            </div>
          )}
        </div>
      )}

      {camps.length === 0 ? (
        <Honest icon={Megaphone} title="No campaigns yet" body="Approve and activate a growth plan and your campaigns appear here. We never mark a campaign live until delivery is confirmed."
          cta={<Link to="/app/campaigns/new" className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 14 }}>Create with AI</Link>} />
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          {camps.map((c) => <CampaignRow key={c.id} c={c} isStaff={isStaff} reload={reload}
            selected={selected.has(c.id)} onToggleSelect={toggleSelect} />)}
        </div>
      )}
    </div>
  );
}

function CampaignRow({ c, isStaff, reload, selected, onToggleSelect }) {
  const [tracking, setTracking] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [removing, setRemoving] = useState(false);
  const archived = c.status === "ARCHIVED";
  const toggle = async () => {
    if (!tracking) {
      setBusy(true);
      try { const r = await sr.createTracking(c.id); setTracking(r.tracking); } catch { toast.error("Could not create tracking"); } finally { setBusy(false); }
    }
    setOpen(!open);
  };
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const archive = async () => {
    if (!window.confirm(`Archive "${c.name}"? You can restore it later from Show archived.`)) return;
    setRemoving(true);
    try { await sr.deleteCampaign(c.id); toast.success("Campaign archived"); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not archive"); } finally { setRemoving(false); }
  };
  const deleteForever = async () => {
    if (!window.confirm(`Permanently delete "${c.name}"? This cannot be undone.`)) return;
    setRemoving(true);
    try { await sr.deleteCampaign(c.id, true); toast.success("Campaign deleted"); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not delete"); } finally { setRemoving(false); }
  };
  const restore = async () => {
    setRemoving(true);
    try { await sr.restoreCampaign(c.id); toast.success("Campaign restored"); reload?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not restore"); } finally { setRemoving(false); }
  };
  return (
    <div className="sr-card" data-testid={`campaign-${c.id}`} style={archived ? { opacity: 0.7 } : {}}>
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          {!archived && (
            <input type="checkbox" checked={!!selected} onChange={() => onToggleSelect?.(c.id)}
              data-testid={`select-${c.id}`} style={{ width: 16, height: 16, cursor: "pointer" }} />
          )}
          <div>
            <strong style={{ color: "var(--navy)", textTransform: "capitalize" }}>{c.name}</strong>
            <div className="sr-muted" style={{ fontSize: 13 }}>${Number(c.budget_monthly).toLocaleString()}/mo · {c.activation_mode === "API" ? "Automated" : "Managed setup"}</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span className={`sr-badge ${archived ? "sr-badge-gray" : TONE[c.display_tone] || "sr-badge-gray"}`} data-testid={`campaign-status-${c.id}`}>
            {archived ? "Archived" : (c.display_status || c.status)}
          </span>
          {!archived && (
            <>
              <Link to={`/app/campaigns/${c.id}/project`} className="sr-btn sr-btn-ghost sr-btn-sm" data-testid={`project-${c.id}`}>
                <ListChecks size={14} /> Project
              </Link>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={toggle} disabled={busy} data-testid={`tracking-${c.id}`}>
                {busy ? <Loader2 size={14} className="sr-spin" /> : <QrCode size={14} />} Tracking
              </button>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={archive} disabled={removing} data-testid={`archive-${c.id}`} title="Archive campaign">
                {removing ? <Loader2 size={14} className="sr-spin" /> : <Archive size={14} />} Archive
              </button>
            </>
          )}
          {archived && (
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={restore} disabled={removing} data-testid={`restore-${c.id}`}>
              {removing ? <Loader2 size={14} className="sr-spin" /> : <RotateCcw size={14} />} Restore
            </button>
          )}
          {isStaff && (
            <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={deleteForever} disabled={removing} data-testid={`delete-${c.id}`} title="Delete permanently"
              style={{ color: "#C0362C" }}>
              <Trash2 size={14} /> Delete
            </button>
          )}
        </div>
      </div>
      {open && tracking && (
        <div style={{ marginTop: 14, display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center" }} data-testid={`tracking-panel-${c.id}`}>
          <img src={tracking.qr_svg} alt="QR code" style={{ width: 110, height: 110, background: "#fff", borderRadius: 8, border: "1px solid var(--border)" }} />
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="sr-muted" style={{ fontSize: 12 }}>Promo / QR code</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
              <strong style={{ color: "var(--navy)", fontSize: 18, letterSpacing: 1 }}>{tracking.promo_code}</strong>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => copy(tracking.promo_code)}><Copy size={12} /></button>
            </div>
            <div className="sr-muted" style={{ fontSize: 12, marginTop: 10 }}>Redemption link</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <a href={tracking.redeem_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, wordBreak: "break-all" }}>{tracking.redeem_url}</a>
              <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => copy(tracking.redeem_url)}><Copy size={12} /></button>
            </div>
            <div className="sr-muted" style={{ fontSize: 12, marginTop: 10 }}><Phone size={11} /> {tracking.phone_label}</div>
            <div style={{ display: "flex", gap: 14, marginTop: 10 }}>
              <span className="sr-badge sr-badge-green">Redemptions: {tracking.redemptions}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
